import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaTimes, FaUser } from 'react-icons/fa';
import { apiCall } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface Patient {
  id: number;
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  gender?: string;
  client_id?: string;
}

interface PatientSearchDropdownProps {
  value: string;
  onChange: (patientId: string, patientName: string, patientDob?: string, patientGender?: string) => void;
  placeholder?: string;
}

const PatientSearchDropdown: React.FC<PatientSearchDropdownProps> = ({
  value,
  onChange,
  placeholder = 'Search for a patient...',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Patient[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [justSelected, setJustSelected] = useState(false); // Track recent selection
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set right before a prop-driven searchTerm update (e.g. edit mode seeding
  // an already-selected patient's name) so the search effect below can skip
  // that one run -- without this it treats the sync as a live user search
  // and pops the results dropdown back open over an already-resolved value.
  const isProgrammaticUpdateRef = useRef(false);
  const { token } = useAuth();

  // Synchronize external value prop with internal searchTerm state
  useEffect(() => {
    if (value && value !== searchTerm && !selectedPatient) {
      isProgrammaticUpdateRef.current = true;
      setSearchTerm(value);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search patients with debounce
  useEffect(() => {
    // Don't search if we just selected a patient, or if this run was
    // triggered by the prop-sync effect rather than the user typing.
    if (isProgrammaticUpdateRef.current) {
      isProgrammaticUpdateRef.current = false;
      return;
    }
    if (selectedPatient || justSelected) {
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.trim().length < 2) {
      setResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchPatients(searchTerm);
    }, 300); // 300ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, selectedPatient, justSelected]);

  const searchPatients = async (query: string) => {
    if (!token) {
      console.error('[PatientSearch] No auth token available');
      return;
    }
    
    setIsLoading(true);
    try {
      // First try the search endpoint
      let response = await apiCall(`/patients/?search=${encodeURIComponent(query)}`, {
        method: 'GET',
      }, token);

      console.log('[PatientSearch] Search Query:', query);
      console.log('[PatientSearch] Search Response status:', response.status);
      console.log('[PatientSearch] Search Response OK:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('[PatientSearch] Search Full response:', data);
        console.log('[PatientSearch] Search Count:', data.count);
        console.log('[PatientSearch] Search Results length:', data.results?.length);
        
        // Handle both paginated and non-paginated responses
        const patients = data.results || data || [];
        console.log('[PatientSearch] Search Parsed patients:', patients.length, 'items');
        
        // Additional client-side filtering to match typed text more precisely
        const searchLower = query.toLowerCase().trim();
        const filtered = patients.filter((patient: Patient) => {
          const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
          const reverseName = `${patient.last_name} ${patient.first_name}`.toLowerCase();
          const clientId = (patient.client_id || '').toLowerCase();
          
          return fullName.includes(searchLower) || 
                 reverseName.includes(searchLower) || 
                 clientId.includes(searchLower);
        });
        
        if (filtered.length > 0) {
          console.log('[PatientSearch] Search First filtered patient:', filtered[0]);
          setResults(filtered);
          setIsOpen(true);
          return;
        }
      }

      // If search endpoint doesn't return results, try fallback: get all patients and filter client-side
      console.log('[PatientSearch] Search returned no results, trying fallback...');
      response = await apiCall('/patients/', {
        method: 'GET',
      }, token);

      if (response.ok) {
        const data = await response.json();
        console.log('[PatientSearch] Fallback Full response count:', data.count);
        
        const allPatients = data.results || data || [];
        console.log('[PatientSearch] Fallback Total patients:', allPatients.length);
        
        // Filter patients client-side with more precise matching
        const queryLower = query.toLowerCase().trim();
        const filteredPatients = allPatients.filter((patient: Patient) => {
          const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
          const reverseName = `${patient.last_name} ${patient.first_name}`.toLowerCase();
          const clientId = (patient.client_id || '').toLowerCase();
          
          return fullName.includes(queryLower) || 
                 reverseName.includes(queryLower) || 
                 clientId.includes(queryLower);
        });

        console.log('[PatientSearch] Fallback Filtered patients:', filteredPatients.length, 'items');
        
        if (filteredPatients.length > 0) {
          console.log('[PatientSearch] Fallback First filtered patient:', filteredPatients[0]);
          setResults(filteredPatients.slice(0, 10)); // Limit to 10 results
          setIsOpen(true);
        } else {
          setResults([]);
          setIsOpen(true);
        }
      } else {
        console.error('[PatientSearch] Fallback failed with status:', response.status);
        const errorText = await response.text();
        console.error('[PatientSearch] Fallback Error response:', errorText);
        setResults([]);
      }
    } catch (error) {
      console.error('Error searching patients:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (patient: Patient) => {
    const fullName = `${patient.first_name} ${patient.last_name}`;
    const formattedDob = formatDOB(patient.date_of_birth);
    setSelectedPatient(patient);
    setSearchTerm(fullName);
    setResults([]); // Clear results to prevent reopening
    setIsOpen(false);
    setJustSelected(true); // Mark that we just selected
    onChange(patient.id.toString(), fullName, formattedDob, patient.gender);
    
    // Reset justSelected flag after a short delay
    setTimeout(() => {
      setJustSelected(false);
    }, 100);
    
    // Blur the input to prevent immediate refocus
    if (dropdownRef.current) {
      const input = dropdownRef.current.querySelector('input');
      if (input) {
        input.blur();
      }
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setSelectedPatient(null);
    setResults([]);
    onChange('', '');
    setIsOpen(false);
  };

  const formatDOB = (dob?: string) => {
    if (!dob) return '';
    const date = new Date(dob);
    return date.toLocaleDateString('en-AU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {isLoading ? (
            <span className="loading loading-spinner loading-sm"></span>
          ) : (
            <FaSearch className="text-base-content opacity-40" />
          )}
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            // Clear selected patient when user starts typing again
            if (selectedPatient) {
              setSelectedPatient(null);
            }
            setJustSelected(false); // Clear the flag when typing
            if (e.target.value.length >= 2) {
              setIsOpen(true);
            }
          }}
          onFocus={() => {
            // Don't open if we just selected something or if a patient is already selected
            if (justSelected || selectedPatient) {
              return;
            }
            // Only open dropdown if there are results
            if (searchTerm.length >= 2 && results.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          className="input input-bordered w-full pl-10 pr-10"
        />
        {searchTerm && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            type="button"
          >
            <FaTimes className="text-base-content opacity-40 hover:opacity-70" />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {isOpen && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {results.map((patient) => (
            <button
              key={patient.id}
              onMouseDown={(e) => {
                // Prevent the input from losing and regaining focus
                e.preventDefault();
                handleSelect(patient);
              }}
              className="w-full text-left px-4 py-3 hover:bg-base-200 border-b border-base-300 last:border-b-0 transition-colors"
              type="button"
            >
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <FaUser className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-base-content">
                    {patient.first_name} {patient.last_name}
                  </div>
                  <div className="text-sm text-base-content opacity-70 flex gap-4 mt-1">
                    {patient.date_of_birth && (
                      <span>DOB: {formatDOB(patient.date_of_birth)}</span>
                    )}
                    {patient.client_id && (
                      <span>ID: {patient.client_id}</span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No Results */}
      {isOpen && searchTerm.length >= 2 && results.length === 0 && !isLoading && (
        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg p-4 text-center text-base-content opacity-70">
          No patients found matching "{searchTerm}"
          <div className="text-xs opacity-60 mt-1">
            Searched both server and local database
          </div>
        </div>
      )}

      {/* Min Characters Notice */}
      {searchTerm.length > 0 && searchTerm.length < 2 && (
        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg p-4 text-center text-base-content opacity-70 text-sm">
          Type at least 2 characters to search
        </div>
      )}
    </div>
  );
};

export default PatientSearchDropdown;
